import {
    Box,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuItemOption,
    MenuGroup,
    MenuOptionGroup,
    MenuDivider,
    Button,
    Text,
    Tooltip,
    HStack,
    VStack,
    Collapse
} from '@chakra-ui/react';
import mapboxgl from 'mapbox-gl';
import React, { useContext, useEffect, useRef, useState } from 'react';
import MapContext from '../../context/MapContext';
import CompassControl from '@mapbox-controls/compass';
import RulerControl from '../../utils/mapbox/ruler';
import ZoomControl from '@mapbox-controls/zoom';
import { MapContextMenu } from './MapContextMenu';
import UIContext from '../../context/UIContext';
import { useHistory, useLocation } from 'react-router-dom';
import useIsDemo from '../../hooks/useIsDemo';
import useIsChartCoverageDemo from '../../hooks/useIsChartCoverageDemo';
// import 'mapbox-gl-inspect/dist/mapbox-gl-inspect.css';
// import MapboxInspect from 'mapbox-gl-inspect';
import * as turf from "@turf/turf";
import { Icon } from '@blueprintjs/core';
import MobileToolbar from '../mobileToolbar/MobileToolbar';
import cx from 'classnames';
import MeasureDisplay from './MeasureDisplay';
import RoutingDisplay from './RoutingDisplay';
import { useLocalStorage } from '@uidotdev/usehooks';
import FrameRateControl from '../../utils/mapbox/framerate';

const rasterUrl = `${import.meta.env.VITE_ENC_API_URL_PREFIX}/api/v1/rasterTiles/{z}/{x}/{y}.png?token=${import.meta.env.VITE_VECTOR_CHARTS_API_TOKEN}`;

const rasterStyle = {
    'version': 8,
    'sources': {
        'raster-tiles': {
            'type': 'raster',
            'tiles': [rasterUrl],
            'tileSize': 256,
        }
    },
    'layers': [
        {
            'id': 'simple-tiles',
            'type': 'raster',
            'source': 'raster-tiles',
            'minzoom': 0,
            'maxzoom': 22
        }
    ]
}

function SelectedObjectDisplay({ selectedObject }) {
    const { 
        commitSelectedSearchResult } = useContext(UIContext);
    const [viewRaw, setViewRaw] = useLocalStorage('show-raw-data', false);
    const {
        id,
        parentId,
        parentCellName,
        layerId,
        layerName,
        position,
        attributes,
        rawAttributes
    } = selectedObject;

    const history = useHistory();

    return (
            <Box    
                pointerEvents="auto"
                zIndex={20}
                display="flex"
                flexDir="column"
                justifyContent="stretch"
                alignItems="stretch"
                bg="rgba(255,255,255,0.9)"
                borderRadius="4px"
                border="1px solid rgba(0,0,0,0.1)"
                shadow="md"
                padding="10px"
                overflow="hidden"
                boxShadow={'0px 0px 0px 2px rgba(0,0,0,0.1)'}
                position="relative"
            >
                Selected object:
                <Text></Text>
                <Text>{attributes.objectName}</Text>
                <Text>{layerName} ({layerId})</Text>
                <HStack>
                <Text flex="1" pt="4px" color="rgba(0,0,0,0.4)">{parentCellName}, {position[0].toFixed(6)}, {position[1].toFixed(6)}</Text>
                <Button mb="-4px" mr="-4px" fontWeight="500" size="sm" variant="text" onClick={() => setViewRaw(!viewRaw)}>{!viewRaw ? "Show" : "Hide"} Data</Button>
                </HStack>
                <Collapse in={viewRaw}>
                <Box
                    userSelect="all" pointerEvents="auto"
                    p="10px" mt="10px" ml="-0px" mr="-10px" mb="-10px"
                    borderTop="1px solid rgba(0,0,0,0.1)" maxW="400px"
                    fontFamily="monospace" fontWeight="600" fontSize="12px"
                >{JSON.stringify(rawAttributes, (key, value) => {
                    if (value !== null) return value
                    })}</Box>
                </Collapse>
                <Tooltip placement="right" label={<Text>Deselect</Text>}>
                    <Button
                        _hover={{
                            color: 'rgba(0,0,0,1.0)',
                        }}
                        bg={''}
                        w="40px"
                        h={'40px'}
                        position="absolute"
                        top="0px"
                        right="0px"
                        onClick={() => {
                            commitSelectedSearchResult(null);
                            const params = new URLSearchParams(window.location.search);
                            params.delete('sid');
                            history.replace({ pathname: window.location.pathname, search: params.toString() });   
                        }}
                        color={'rgba(0,0,0,0.8)'}
                    >
                        <Text mb="0px">
                            <Icon icon={'cross'} size="16" />
                        </Text>
                    </Button>
                </Tooltip>
            </Box>
    )
}
function Map() {
    const { 
        rulerControlRef,
                measureState,
                setMeasureState,
                raster, markers, styleUrl, setMarkers, mapRef, style, styleSettings, contextMenuLocation, setContextMenuLocation} =
        useContext(MapContext);
    const mapContainer = useRef(null);
    const lastMousePos = useRef(null);
    const lastStyleUrl = useRef(null);
    const markerState = useRef({});
    const routeMarkerState = useRef({});
    const routeSource = useRef(null);
    const trackSource = useRef(null);
    const selectedSearchResultSource = useRef(null);
    const searchRadiusSource = useRef(null);
    const controlsLoaded = useRef(null);
    const dashAnimationFrameRef = useRef(null);
    const isDemo = useIsDemo();

    const history = useHistory();
    const location = useLocation();

    const updatePathLocation = (lat, lon, zoom) => {
        const params = new URLSearchParams(window.location.search);
        params.set('lat', lat.toFixed(6));
        params.set('lon', lon.toFixed(6));
        params.set('zoom', zoom.toFixed(2));
        history.replace({ pathname: window.location.pathname, search: params.toString() });       
    }

    const routeQueryState = useRef({});
    const trackState = useRef({});

    const { routeInputWaypoints, setRouteInputWaypoints, routeResults, selectedSearchResult, searchQuery, forceReload, setForceReload, isIosApp, insets,mobileToolbarOpen, track } = useContext(UIContext);

    const isChartCoverageDemo = useIsChartCoverageDemo();

    // const demoAngle = useRef(0);
    // useEffect(() => {
    //     const int = setInterval(() => {
    //         if (!mapRef.current) {
    //             return;
    //         }

    //         demoAngle.current += 0.3;
    //         if (demoAngle.current > 360.0) {
    //             demoAngle.current -= 360.0;
    //         }
    //         mapRef.current.easeTo({
    //             bearing: demoAngle.current,
    //             duration: 2000,
    //             easing: x => x
    //         });
    //     }, 100);

    //     return () => {
    //         clearInterval(int);
    //     };
    // }, []);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const startLat = searchParams.get('lat') || 42.313874;
        const startLon = searchParams.get('lon') || -70.951109;
        const startZoom = searchParams.get('zoom') || 12;
        
        lastStyleUrl.current = raster ? "raster" : styleUrl;

        mapRef.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: raster ? rasterStyle : styleUrl,
            projection: 'globe',
            center: [startLon, startLat],
            zoom: startZoom,
            maxZoom: isChartCoverageDemo ? 7 : 19,
        });
        window.mapRef = mapRef.current;
        mapRef.current.setMaxPitch(80);
        mapRef.current.setMinPitch(0);
        mapRef.current.dragRotate.disable();

        if (isDemo || isChartCoverageDemo) {
            mapRef.current.scrollZoom.disable();
        }

        mapRef.current.on('moveend', (e) => {
            const center = mapRef.current.getCenter();
            updatePathLocation(center.lat, center.lng, mapRef.current.getZoom());
        });

        mapRef.current.on('touchmove', (e) => {
            lastMousePos.current = {
                latitude: e.lngLat.lat,
                longitude: e.lngLat.lng,
            };
            
        });
        mapRef.current.on('mousemove', (e) => {
            lastMousePos.current = {
                latitude: e.lngLat.lat,
                longitude: e.lngLat.lng,
            };
        });
        mapRef.current.on('zoom', (e) => {
            setContextMenuLocation(null);
        });

        
        mapRef.current.on('style.load', () => {
            if (!controlsLoaded.current) {
                rulerControlRef.current = new RulerControl({
                    onMeasureStateChange: (obj) => { setMeasureState(obj); }
                });
                mapRef.current.addControl(rulerControlRef.current, 'bottom-right');
                mapRef.current.addControl(new ZoomControl(), 'bottom-right');
                // mapRef.current.addControl(new FrameRateControl({}))
                mapRef.current.addControl(
                    new CompassControl({
                        instant: true,
                    }),
                    'bottom-right'
                );
                controlsLoaded.current = true;
            }
                
            if (isChartCoverageDemo) {
                mapRef.current.setFog({
                    range: [2, 20],
                    color: 'rgb(255, 255, 255)',
                    'high-color': 'rgb(255, 255, 255)',
                    'space-color': [
                        'interpolate',
                        ['exponential', 1.2],
                        ['zoom'],
                        5,
                        'hsl(0, 0%, 90%)',
                        7,
                        'hsl(0, 0%, 90%)',
                    ],
                    'star-intensity': 0,
                });
            } else {
                mapRef.current.setFog({
                    range: [2, 20],
                    color: 'rgb(255, 255, 255)',
                    'high-color': 'rgb(153, 204, 255)',
                    'horizon-blend': ['interpolate', ['exponential', 1.2], ['zoom'], 5, 0.02, 7, 0.08],
                    'space-color': [
                        'interpolate',
                        ['exponential', 1.2],
                        ['zoom'],
                        5,
                        'hsl(210, 40%, 30%)',
                        7,
                        'hsl(210, 100%, 80%)',
                    ],
                    'star-intensity': ['interpolate', ['exponential', 1.2], ['zoom'], 5, 0.1, 7, 0],
                });
            }

            
        });
        mapRef.current.on('load', () => {
            routeSource.current = mapRef.current.addSource('route-path-display', {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'LineString',
                        coordinates: [],
                    },
                },
            });
            routeSource.current = mapRef.current.addSource('track-path-display', {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'LineString',
                        coordinates: [],
                    },
                },
            });
            searchRadiusSource.current = mapRef.current.addSource('search-radius-display', {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'Polygon',
                        coordinates: [],
                    },
                },
            });
            selectedSearchResultSource.current = mapRef.current.addSource('search-result-display', {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'LineString',
                        coordinates: [],
                    },
                },
            });
            mapRef.current.addLayer({
                id: 'route-path-display-1',
                type: 'line',
                source: 'route-path-display',
                filter: ['==', ['get', 'success'], 1],
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                },
                paint: {
                    'line-color': '#000000',
                    'line-width': 8,
                    'line-opacity': 0.5,
                },
            });
            mapRef.current.addLayer({
                id: 'route-path-display-2',
                type: 'line',
                source: 'route-path-display',
                filter: ['==', ['get', 'success'], 1],
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                },
                paint: {
                    'line-color': '#ffffff',
                    'line-width': 4,
                },
            });
            mapRef.current.addLayer({
                id: 'route-path-display-3',
                type: 'line',
                source: 'route-path-display',
                filter: ['==', ['get', 'success'], 0],
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                },
                paint: {
                    'line-color': '#000000',
                    'line-width': 8,
                    'line-opacity': 0.25,
                },
            });
            mapRef.current.addLayer({
                id: 'route-path-display-4',
                type: 'line',
                source: 'route-path-display',
                filter: ['==', ['get', 'success'], 0],
                layout: {
                    'line-join': 'bevel',
                    'line-cap': 'butt',
                },
                paint: {
                    'line-color': '#ffffff',
                    'line-width': 4,
                    'line-dasharray': [2,2]
                },
            });
            mapRef.current.addLayer({
                id: 'route-path-display-5',
                type: 'line',
                source: 'route-path-display',
                filter: ['==', ['get', 'success'], 2],
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                },
                paint: {
                    'line-color': '#000000',
                    'line-width': 8,
                    'line-opacity': 0.25,
                },
            });
            mapRef.current.addLayer({
                id: 'route-path-display-6',
                type: 'line',
                source: 'route-path-display',
                filter: ['==', ['get', 'success'], 2],
                layout: {
                    'line-join': 'bevel',
                    'line-cap': 'butt',
                },
                paint: {
                    'line-color': '#ffc3c3',
                    'line-width': 4,
                    'line-dasharray': [2,2]
                },
            });
            mapRef.current.addLayer({
                id: 'route-path-display-7',
                type: 'symbol',
                source: 'route-path-display',
                filter: ['==', ['get', 'success'], 2],
                layout: {
                    'text-field': 'Routing Failed',
                    'symbol-placement': 'line-center',
                    'text-ignore-placement': true,
                    'text-allow-overlap': true,
                    'text-justify': 'center',
                    'text-size': 20
                },
                paint: {
                    'text-color': '#ffc3c3',
                    'text-halo-color': 'rgba(0, 0, 0, 0.5)',
                    'text-halo-width': 2
                },
            });


            mapRef.current.addLayer({
                id: 'track-path-display-1',
                type: 'line',
                source: 'track-path-display',
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                },
                paint: {
                    'line-color': '#000000',
                    'line-width': 8,
                    'line-opacity': 0.4,
                },
            });
            mapRef.current.addLayer({
                id: 'track-path-display-2',
                type: 'line',
                source: 'track-path-display',
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                },
                paint: {
                    'line-color': 'rgb(113, 191, 255)',
                    'line-width': 4,
                },
            });

            mapRef.current.addLayer({
                id: 'search-radius-display-1',
                type: 'line',
                source: 'search-radius-display',
                layout: {
                    'line-join': 'miter',
                    'line-cap': 'butt',
                },
                paint: {
                    'line-color': '#302929',
                    'line-width': 4,
                    'line-opacity': 0.5,
                    'line-dasharray': [4,3]
                },
            });

            mapRef.current.addLayer({
                id: 'search-result-display-1',
                type: 'line',
                source: 'search-result-display',
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                },
                paint: {
                    'line-color': '#000000',
                    'line-width': 8,
                    'line-opacity': 0.5,
                },
            });
            mapRef.current.addLayer({
                id: 'search-result-display-2',
                type: 'line',
                source: 'search-result-display',
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                },
                paint: {
                    'line-color': '#ff8800',
                    'line-width': 4,
                },
            });
            console.log("Map style loaded. Forcing data reload...");
            setForceReload(Math.random());

            // Animate path line style.
            let step = 0;
            const dashArraySequence = [
                [0, 4, 3],
                [0.5, 4, 2.5],
                [1, 4, 2],
                [1.5, 4, 1.5],
                [2, 4, 1],
                [2.5, 4, 0.5],
                [3, 4, 0],
                [0, 0.5, 3, 3.5],
                [0, 1, 3, 3],
                [0, 1.5, 3, 2.5],
                [0, 2, 3, 2],
                [0, 2.5, 3, 1.5],
                [0, 3, 3, 1],
                [0, 3.5, 3, 0.5]
            ];
            function animateDashArray(timestamp) {
                if (!mapRef.current || !mapRef.current.isStyleLoaded() || !mapRef.current.getLayer('route-path-display-4')) {
                    dashAnimationFrameRef.current = requestAnimationFrame(animateDashArray);
                    return;
                }
                const newStep = parseInt(
                    (timestamp / 50) % dashArraySequence.length
                );

                if (newStep !== step) {
                    mapRef.current.setPaintProperty(
                        'route-path-display-4',
                        'line-dasharray',
                        dashArraySequence[step]
                    );
                    step = newStep;
                }

                // Request the next frame of the animation.
                dashAnimationFrameRef.current = requestAnimationFrame(animateDashArray);
            }

            // start the animation
            animateDashArray(0);
        });
        return () => {
            if (dashAnimationFrameRef.current) {
                cancelAnimationFrame(dashAnimationFrameRef.current);
                dashAnimationFrameRef.current = null;
            }
            mapRef.current.remove();
        };
    }, [mapRef]);

    useEffect(() => {
        if (!mapRef.current.getSource('search-radius-display')) {
            return;
        }
        if (searchQuery) {
            var circle = turf.circle([searchQuery.position.longitude,
                searchQuery.position.latitude
            ], searchQuery.radius, {
                units: "meters",
                steps: 180
            });

            mapRef.current.getSource('search-radius-display').setData(turf.getGeom(circle));
        } else {
            console.log("Clearing search radius display")
            mapRef.current.getSource('search-radius-display').setData({
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'LineString',
                    coordinates: [],
                },
            });
        }
    }, [searchQuery]);

    useEffect(() => {
        if (!mapRef.current.getSource('search-result-display')) {
            console.log('No search result display layer.');
            return;
        }
        if (selectedSearchResult) {
            console.log(selectedSearchResult)
            mapRef.current.getSource('search-result-display').setData(selectedSearchResult.geometry);

            const bounds = new mapboxgl.LngLatBounds(
                {
                    lng: selectedSearchResult.bbox[0][0],
                    lat: selectedSearchResult.bbox[0][1],
                },
                {
                    lng: selectedSearchResult.bbox[0][0],
                    lat: selectedSearchResult.bbox[0][1],
                },
            );
            for (const coord of selectedSearchResult.bbox) {
                bounds.extend({
                    lng: coord[0],
                    lat: coord[1],
                });
            }
            // mapRef.current.fitBounds(bounds, {
            //     padding: window.innerWidth / 3
            // });
            mapRef.current.flyTo({
                center: [selectedSearchResult.position[1],selectedSearchResult.position[0]],
                zoom: mapRef.current.getZoom(),
            });
        } else {
            mapRef.current.getSource('search-result-display').setData({
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'LineString',
                    coordinates: [],
                },
            });
        }
    }, [selectedSearchResult]);

    useEffect(() => {
        let i = 1;
        const staleMarkers = {};
        for (const id of Object.keys(routeMarkerState.current)) {
            staleMarkers[id] = true;
        }
        for (const waypoint of routeInputWaypoints || []) {
            staleMarkers[waypoint.id] = false;

            if (!(waypoint.id in routeMarkerState.current)) {
                routeMarkerState.current[waypoint.id] = {
                    marker: null,
                    el: document.createElement('div'),
                    data: waypoint
                }

                routeMarkerState.current[waypoint.id].el.innerText = i;
                routeMarkerState.current[waypoint.id].el.className = 'route-marker';
                routeMarkerState.current[waypoint.id].marker = new mapboxgl.Marker(routeMarkerState.current[waypoint.id].el, {
                    draggable: true,
                })

                routeMarkerState.current[waypoint.id].marker
                    .setLngLat([waypoint.longitude, waypoint.latitude])
                    .addTo(mapRef.current);
                routeMarkerState.current[waypoint.id].marker.on('drag', () => {
                    setRouteInputWaypoints((oldWaypoints) => {
                        const newLngLat = routeMarkerState.current[waypoint.id].marker.getLngLat();

                        const newArray = [];
                        for (let oldWaypoint of oldWaypoints) {
                            if (oldWaypoint.id == waypoint.id) {
                                oldWaypoint.latitude = newLngLat.lat;
                                oldWaypoint.longitude = newLngLat.lng;
                            }
                            newArray.push(oldWaypoint);
                        }
                        return newArray;
                    });
                });
            }
            i++;
        }

        for (const id of Object.keys(staleMarkers)) {
            if (staleMarkers[id] == true) {
                routeMarkerState.current[id].marker.remove();
                delete routeMarkerState.current[id];
            }
        }
    }, [routeInputWaypoints, setRouteInputWaypoints, mapRef]);

    useEffect(() => {
        if (!mapRef.current) {
            return;
        }
        if (routeResults && routeResults.success && !routeResults.loading) {
            const waypoints = routeResults?.route || [];
            const routePathSource = mapRef.current.getSource('route-path-display');
            if (!routePathSource) {
                return;
            }
            if (waypoints.length == 0) {
                if (routePathSource) {
                    routePathSource.setData({
                    type: 'Feature',
                    properties: {
                        success: 1
                    },
                    geometry: {
                        type: 'LineString',
                        coordinates: [],
                    },
                });
                }
                return;
            }
            
            const geojson = turf.lineString(waypoints.map((waypoint) => (
                [
                    waypoint[1],
                    waypoint[0]
                ]
            )));
            routePathSource.setData({
                ...geojson,
                properties: {
                    success: 1
                }
            });
        } else {
            // Display placeholder path.
            const routePathSource = mapRef.current.getSource('route-path-display');
            if (!routePathSource) {
                return;
            }

            if (routeInputWaypoints.length <= 1) {
                if (routePathSource) {
                    routePathSource.setData({
                    type: 'Feature',
                    properties: {
                        success: routeResults?.loading ? 0 : 2
                    },
                    geometry: {
                        type: 'LineString',
                        coordinates: [],
                    },
                });
                }
                return;
            }
            
            const geojson = turf.lineString(routeInputWaypoints.map((waypoint) => (
                [
                    waypoint.longitude,
                    waypoint.latitude
                ]
            )));
            routePathSource.setData({
                ...geojson,
                properties: {
                    success: routeResults?.loading ? 0 : 2
                }
            });
        }
    }, [routeResults, routeInputWaypoints, forceReload]);


    useEffect(() => {
        const latestPos = track?.latest || null;
        if (!latestPos && trackState?.current?.marker) {
            trackState.current.marker.remove();
            trackState.current.marker = null;
        } else if (latestPos) {
            let effectiveHeading = mapRef.current.getBearing();
            if (latestPos.heading && (latestPos.heading > 0)) {
                effectiveHeading = latestPos.heading;
            }
            mapRef.current.flyTo({
                center: [latestPos.position[1], latestPos.position[0]],
                zoom: mapRef.current.getZoom(),
                bearing: effectiveHeading
            });
            if (!trackState.current.marker) {
                const el = document.createElement('div');
                el.className = 'track-marker';
                trackState.current.marker = new mapboxgl.Marker(el, {
                    draggable: false,
                });
                trackState.current.marker.setLngLat([latestPos.position[1], latestPos.position[0]]).addTo(mapRef.current);
            }
            trackState.current.marker.setLngLat([latestPos.position[1], latestPos.position[0]]);
        }

        if (mapRef.current.getSource('track-path-display')) {
            mapRef.current.getSource('track-path-display').setData({
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'LineString',
                    coordinates: track?.history?.map((pt) => [pt.position[1], pt.position[0]]) || [],
                },
            });
        }
    }, [track, mapRef]);

    useEffect(() => {
        const styleId = raster ? "raster" : styleUrl;
        if (lastStyleUrl.current !== (styleId)) {
            // Only change style url when necessary.
            console.log(`Old style: ${lastStyleUrl.current}`);
            console.log(`New style: ${styleId}`);
            if (dashAnimationFrameRef.current) {
                cancelAnimationFrame(dashAnimationFrameRef.current);
                dashAnimationFrameRef.current = null;
            }
            mapRef.current.setStyle(raster ? rasterStyle : styleUrl);
            lastStyleUrl.current = styleId;
        }
        mapRef.current.showTileBoundaries = styleSettings.showTileBoundaries;
        mapRef.current.showTileAABBs = styleSettings.showTileBoundaries;
        mapRef.current.showCollisionBoxes = styleSettings.showTileBoundaries;

        try {
            if (isChartCoverageDemo) {
                mapRef.current.setFog({
                    range: [2, 20],
                    color: 'rgb(255, 255, 255)',
                    'high-color': 'rgb(255, 255, 255)',
                    'space-color': [
                        'interpolate',
                        ['exponential', 1.2],
                        ['zoom'],
                        5,
                        'hsl(0, 0%, 90%)',
                        7,
                        'hsl(0, 0%, 90%)',
                    ],
                    'star-intensity': 0,
                });
            } else if (style == 'day') {
                // mapRef.current.setFog({
                //     range: [2, 20],
                //     color: 'rgb(255, 255, 255)',
                //     'high-color': 'rgb(153, 204, 255)',
                //     'horizon-blend': ['interpolate', ['exponential', 1.2], ['zoom'], 5, 0.02, 7, 0.08],
                //     'space-color': [
                //         'interpolate',
                //         ['exponential', 1.2],
                //         ['zoom'],
                //         5,
                //         'hsl(210, 40%, 30%)',
                //         7,
                //         'hsl(210, 100%, 80%)',
                //     ],
                //     'star-intensity': ['interpolate', ['exponential', 1.2], ['zoom'], 5, 0.1, 7, 0],
                // });
                mapRef.current.setFog({
                    range: [2, 20],
                    color: 'rgb(255, 255, 255)',
                    'high-color': 'rgb(153, 204, 255)',
                    'horizon-blend': ['interpolate', ['exponential', 1.2], ['zoom'], 5, 0.02, 7, 0.08],
                    'space-color': [
                        'interpolate',
                        ['exponential', 1.2],
                        ['zoom'],
                        5,
                        'hsl(210, 40%, 30%)',
                        7,
                        'hsl(210, 100%, 80%)',
                    ],
                    'star-intensity': ['interpolate', ['exponential', 1.2], ['zoom'], 5, 0.1, 7, 0],
                });
            } else if (style == 'dusk') {
                mapRef.current.setFog({
                    range: [2, 20],
                    color: 'rgb(143, 143, 143)',
                    'high-color': 'rgb(108, 147, 185)',
                    'horizon-blend': 0.1,
                    'space-color': 'hsl(210, 79%, 23%)',
                    'star-intensity': ['interpolate', ['exponential', 1.2], ['zoom'], 5, 0.1, 7, 0],
                });
            } else if (style == 'night') {
                mapRef.current.setFog({
                    range: [2, 20],
                    color: 'rgb(29, 29, 29)',
                    'high-color': 'rgb(35, 45, 56)',
                    'horizon-blend': ['interpolate', ['exponential', 1.2], ['zoom'], 5, 0.02, 7, 0.08],
                    'space-color': [
                        'interpolate',
                        ['exponential', 1.2],
                        ['zoom'],
                        5,
                        'hsl(210, 40%, 30%)',
                        7,
                        'hsl(210, 26%, 33%)',
                    ],
                    'star-intensity': ['interpolate', ['exponential', 1.2], ['zoom'], 5, 0.1, 7, 0],
                });
            }
        } catch (e) {}
    }, [styleUrl, raster, style, styleSettings, isChartCoverageDemo]);

    const handleClick = (e) => {
        if (e.nativeEvent.button != 2) {
            setContextMenuLocation(false);
            return;
        }

        e.stopPropagation();
        e.preventDefault();

        setContextMenuLocation({
            x: e.clientX,
            y: e.clientY,
            hasLatLon: lastMousePos.current,
            latitude: lastMousePos.current?.latitude || 0,
            longitude: lastMousePos.current?.longitude || 0,
        });
    };

    const handleMouseDown = (e) => {
        if (e.nativeEvent.button != 2) {
            setContextMenuLocation(false);
            return;
        }
    };

    const handlePointerDown = (e) => {};

    const handlePointerUp = (e) => {};

    return (
        <Box position="relative" display="flex" flexDir="column" justifyContent="stretch" alignItems="stretch" flex="1"
                className={cx(mobileToolbarOpen && 'mapMobileToolbarOpen', isIosApp && 'mapMobile')}>
            <Box
                zIndex={1}
                ref={mapContainer}
                width="100%"
                height="100%"
                onClick={handleClick}
                onContextMenu={handleClick}
                // onMouseDown={handleMouseDown}
                // onPointerDown={handlePointerDown}
                // onPointerUp={handlePointerUp}
            ></Box>
            <MapContextMenu></MapContextMenu>

            {(selectedSearchResult || measureState || routeInputWaypoints.length > 0) && (
            <Box
                position="absolute"
                left="40px"
                right="40px"
                bottom="0px"
                userSelect="none"
                pointerEvents="none"
                display="flex"
                justifyContent="center"
                alignItems="center"
                padding="20px"
            >
                <VStack spacing={4}>
                    {selectedSearchResult && (
                        <SelectedObjectDisplay selectedObject={selectedSearchResult} />
                    )}
                    {routeInputWaypoints.length > 0 && (
                        <RoutingDisplay />
                    )}
                    {measureState && (
                        <MeasureDisplay />
                    )}
                </VStack>
            </Box>
            )}
            {isIosApp && (
                <MobileToolbar></MobileToolbar>
            )}
        </Box>
    );
}

export default Map;
